import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { HOME_RECENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { PageCards } from '@confluence/page-card/entry-points/PageCards.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import { untitled } from '../homeViewsQueryHelpers.ts';
import { Options, useOptions } from '../Options/index.ts';
import { LoadingAndPagination } from '../LoadingAndPagination.tsx';
import { ExperienceTracking } from '../ExperienceTracking.tsx';
import { CreatePageButton } from '../CreatePageButton.tsx';
import RecentEmptyStateLight from '../images/recent/RecentEmptyStateLight.svg';
import RecentEmptyStateDark from '../images/recent/RecentEmptyStateDark.svg';
import { HOME_RECENTS_VIEW_SEGMENT } from '../../../perf.config.ts';

import { useRecentData } from './useRecentData.ts';
import {
	type RecentFilter,
	useRecentPreferences,
	useAnonymousRecentPreference,
	useRecentPreferencesOverride,
} from './useRecentPreferences.ts';

const i18nBase = defineMessages({
	recentTitle: {
		id: 'home.views.recent.title',
		defaultMessage: 'Recent',
		description: 'The text is shown as a section heading at the top of the Recent view on the Home page, labelling the list of pages the user has recently visited or edited.',
	},
	all: {
		id: 'home.views.recent.options.all',
		defaultMessage: 'All',
		description: 'Label for a button to view all the users recent pages',
	},
	collaboratedOn: {
		id: 'home.views.recent.options.collaborated-on',
		defaultMessage: 'Collaborated on',
		description: 'Label for a button to view all pages a user has collaborated on',
	},
	workedOn: {
		id: 'home.views.recent.options.worked-on',
		defaultMessage: 'Worked on',
		description: 'Label for a button to view all pages a user has worked on',
	},
	created: {
		id: 'home.views.recent.options.created-by-me',
		defaultMessage: 'Created by me',
		description: 'Label for a button to view all pages a user has created',
	},
	owned: {
		id: 'home.views.recent.options.owned-by-me',
		defaultMessage: 'Owned by me',
		description: 'Label for a button to view all pages a user has owned',
	},
	noRecentResults: {
		id: 'home.views.recent.no-results.no-recent-results',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We can't find any pages visited in the last 90 days that match.",
		description: 'Header shown when all recent pages have been filtered out',
	},
	noResultsSubtext: {
		id: 'home.views.recent.no-results.no-recent-results-subtext',
		defaultMessage: '<a>Search older pages instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	noRecentCreatedByMeResults: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We can't find any pages you've created that match.",
		description: 'Header shown when all recent pages have been filtered out',
	},
	noResultsCreatedByMeSubtext: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results-subtext',
		defaultMessage: '<a>Search pages created by anyone instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	emptyHeader: {
		id: 'home.views.recent.empty.header',
		defaultMessage: 'Get out there and explore',
		description:
			'Descriptive header that appears on the Recent Pages Home view when the user has no visited Pages.',
	},
	emptyBody: {
		id: 'home.views.recent.empty.body',
		defaultMessage: 'Once you visit, edit, or create pages, you’ll find the most recent ones here.',
		description:
			'Descriptive body text that appears on the Recent Pages Home view when the user has no visited Pages.',
	},
});

const i18nLiveDocs = defineMessages({
	noRecentResults: {
		id: 'home.views.recent.no-results.no-recent-results.livedocs',
		defaultMessage: 'We can’t find any pages or live docs visited in the last 90 days that match.',
		description: 'Updated version of home.views.recent.no-results.no-recent-results',
	},

	noResultsSubtext: {
		id: 'home.views.recent.no-results.no-recent-results-subtext.livedocs',
		defaultMessage: '<a>Search older pages and live docs instead</a>.',
		description: 'Updated version of home.views.recent.no-results.no-recent-results-subtext',
	},

	noRecentCreatedByMeResults: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results.livedocs',
		defaultMessage: 'We can’t find any pages or docs you’ve created that match.',
		description: 'Updated version of home.views.recent.no-results.no-recent-created-by-me-results',
	},

	noResultsCreatedByMeSubtext: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results-subtext.livedocs',
		defaultMessage: '<a>Search pages and docs created by anyone instead</a>.',
		description:
			'Updated version of home.views.recent.no-results.no-recent-created-by-me-results-subtext',
	},

	emptyBody: {
		id: 'home.views.recent.empty.body.livedocs',
		defaultMessage:
			'Once you visit, edit, or create pages or live docs, you’ll find the most recent ones here.',
		description: 'Updated version of home.views.recent.empty.body',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const recentContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const recentHeaderStyles = css({
	font: token('font.heading.large'),
	marginBottom: token('space.200'),
});

const pageCardsContainerStyles = css({
	position: 'relative',
});

const recentEmptyStateImage = {
	src: RecentEmptyStateLight,
	srcDark: RecentEmptyStateDark,
};

// used for fromSource attribute in analytics
const FromSource = {
	all: 'null',
	'worked-on': 'worked-on',
	'collaborated-on': 'collaborated-on',
	created: 'created',
};

const toPrefs = {
	all: 'ALL',
	'worked-on': 'WORKED_ON',
	'collaborated-on': 'COLLABORATED_ON',
	created: 'CREATED',
};

const fromPrefs = {
	ALL: 'all',
	WORKED_ON: 'worked-on',
	COLLABORATED_ON: 'collaborated-on',
	CREATED: 'created',
};

type RecentComponentProps = {
	filter: RecentFilter;
	updateFilter: (filter) => void;
};

export const Recent = () => {
	const intl = useIntl();
	const { isLoggedIn } = useSessionData();
	const { filterOverride, clearFilterOverride } = useRecentPreferencesOverride();
	const recentPreferences = useRecentPreferences();
	const anonymousRecentPreference = useAnonymousRecentPreference();
	const { loading, queryError, mutationError, filter, updateFilter } = isLoggedIn
		? recentPreferences
		: anonymousRecentPreference;
	const filterOrOverride = filterOverride || filter;
	const updateFilterAndClearOverride = (updatedFilter: RecentFilter) => {
		clearFilterOverride();
		updateFilter(updatedFilter);
	};

	return (
		<div data-testid="home_recent" css={recentContainerStyles}>
			<PageSegmentLoadStart metric={HOME_RECENTS_VIEW_SEGMENT} />
			<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.recentTitle)} />
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
			<h1 css={recentHeaderStyles}>
				<FormattedMessage {...i18n.recentTitle} />
			</h1>
			{!loading && (
				<RecentComponent filter={filterOrOverride} updateFilter={updateFilterAndClearOverride} />
			)}
			{queryError && <ErrorDisplay error={queryError} />}
			{mutationError && <ErrorDisplay error={mutationError} />}
		</div>
	);
};

export const RecentComponent: FC<RecentComponentProps> = ({ filter, updateFilter }) => {
	const pills = [
		{ name: 'all', i18n: <FormattedMessage {...i18n.all} /> },
		{ name: 'collaborated-on', i18n: <FormattedMessage {...i18n.collaboratedOn} /> },
		{ name: 'worked-on', i18n: <FormattedMessage {...i18n.workedOn} /> },
		{ name: 'created', i18n: <FormattedMessage {...i18n.owned} /> },
	];

	const intl = useIntl();
	const defaultPill = useMemo(() => (filter ? fromPrefs[filter] : 'all'), [filter]);

	const optionsProps = useOptions(pills, defaultPill);

	const { pageCardData, loading, hasNextPage, loadMore, error } = useRecentData({
		pill: optionsProps.pill,
		filterText: optionsProps.filterText,
		translatedUntitled: intl.formatMessage(untitled),
	});

	const [noResultsMessage, noResultsSubtext] =
		filter === 'CREATED'
			? [i18n.noRecentCreatedByMeResults, i18n.noResultsCreatedByMeSubtext]
			: [i18n.noRecentResults, i18n.noResultsSubtext];

	const modifiedOptionsProps = {
		...optionsProps,
		setPill: (pill) => {
			updateFilter(toPrefs[pill]);
			// @ts-ignore we always pass pills to useOptions and the conditional was untestable
			optionsProps.setPill(pill);
		},
	};

	const attributes = optionsProps.pill
		? { isFromSource: FromSource[optionsProps.pill] }
		: undefined;

	return (
		<UFOSegment name="all-recents-page">
			<Options {...modifiedOptionsProps} pageCardDataLength={pageCardData.length} />
			<div css={pageCardsContainerStyles}>
				{!optionsProps.loading && (
					<PageCards
						appearance={optionsProps.appearance}
						nodes={pageCardData}
						analyticsData={{ source: 'recentScreen', attributes }}
					/>
				)}
				<LoadingAndPagination
					pageCardDataLength={pageCardData.length}
					loading={loading}
					hasNextPage={hasNextPage}
					loadMore={loadMore}
					error={error}
					noResultsMessage={noResultsMessage}
					noResultsSubtext={noResultsSubtext}
					emptyMessageHeader={i18n.emptyHeader}
					emptyMessageBody={i18n.emptyBody}
					emptyMessageImage={recentEmptyStateImage}
					emptyMessageCallToAction={
						<CreatePageButton
							appearance="primary"
							creationLinkSource="home-recent"
							showCreateLivePageButton
						/>
					}
					filterText={optionsProps.filterText}
					source="recentScreen"
					filter={filter}
				/>
				<ExperienceTracking
					experienceName={HOME_RECENT_EXPERIENCE}
					loading={loading}
					error={error}
				/>
				{!loading && <PageSegmentLoadEnd metric={HOME_RECENTS_VIEW_SEGMENT} />}
			</div>
		</UFOSegment>
	);
};
